import { bootstrapApplication } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import { importProvidersFrom, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, HttpClient, withInterceptors } from '@angular/common/http';
import { Directionality } from '@angular/cdk/bidi';
import { authInterceptor } from './app/core/interceptors/auth.interceptor';
import { loadingInterceptor } from './app/core/interceptors/loading.interceptor';
import { errorFeedbackInterceptor } from './app/core/interceptors/error-feedback.interceptor';
import { AuthService } from './app/core/auth/services/auth.service';
import { migrateAuthStorage } from './app/core/auth/auth-session.util';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { environment } from './environments/environment';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { LocaleService, readStoredLang } from './app/ui/core/locale.service';

/**
 * Bust the browser cache when `en.json` / `ar.json` gain keys without the
 * bundle hash changing.
 *
 * The two JSON files are served as plain assets, so a returning vendor keeps
 * the copy it already has and renders raw keys (`COUPONS.TITLE`) for every
 * string added since. The admin dashboard has carried this for a while
 * (`main.ts:21`); the vendor loader did not, which is decision D1 of the
 * MarTech plan. Bump the value in the same commit as any i18n addition.
 *
 * The query string is safe for every interceptor: `error-feedback` skips on
 * `url.includes('i18n/')`, `auth` only touches the API base, and `loading`
 * ignores GETs.
 */
const I18N_CACHE_BUST = '20261015-ops-pages';

// Robust factory that avoids DI for string params
export function jsonLoader(http: HttpClient): TranslateLoader {
  return {
    getTranslation: (lang: string) =>
      http.get(`./assets/i18n/${lang}.json?v=${I18N_CACHE_BUST}`),
  } as TranslateLoader;
}

/**
 * Arabic dates. Without this every `| date` rendered en-US regardless of the
 * active language, so an operator working entirely in Arabic read `Sep 4, 2026`
 * and `1:00 PM` — 36 call sites across bookings, financial and office details.
 *
 * `ar`, deliberately NOT `ar-SA`: the latter makes Intl resolve to the
 * Umm al-Qura calendar, which is a different date, not a translated one.
 * `en-US` needs no registration; it is Angular's built-in default.
 *
 * Digits stay Latin in BOTH languages, and no call site needs pinning to get
 * that: Angular's `ar` locale data uses the `latn` numbering system, so
 * `| number` renders `1,234.5` under `ar` exactly as under `en-US`. Verified in
 * a browser, not assumed. That matches `| money`, which formats with
 * `toFixed(2)` and is immune to LOCALE_ID either way — one numeric style
 * throughout, with only the month and day names translated.
 */
registerLocaleData(localeAr);

bootstrapApplication(AppComponent, {
  providers: [
    {
      // Resolved ONCE, at bootstrap, from the same key LocaleService persists.
      // That is why LocaleService.use() reloads: a locale cannot follow an
      // in-place language switch, so without the reload the copy would change
      // and every date would stay in the previous language.
      provide: LOCALE_ID,
      useFactory: () => (readStoredLang() === 'ar' ? 'ar' : 'en-US'),
    },
    provideRouter(appRoutes),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([authInterceptor, loadingInterceptor, errorFeedbackInterceptor]),
    ),
    {
      // ONE owner of language and direction.
      //
      // BIZ-02: activate AND await the initial language before the first paint.
      // `setDefaultLang` only sets a fallback — it never activates a language —
      // and app.component's translate.use() ran after the view had painted, so
      // the login screen briefly rendered raw AUTH.* keys. trans-web fixed this
      // the same way; both dashboards still had the race.
      //
      // The only change here is WHERE that happens: LocaleService.init() does
      // exactly what this factory used to do (read `localStorage.lang`, default
      // 'en', `setDefaultLang('en')`, await `translate.use(lang)`, swallow a
      // load failure) and additionally writes <html lang dir>, which used to be
      // AppComponent's and NavComponent's job on a later tick.
      provide: APP_INITIALIZER,
      multi: true,
      deps: [LocaleService],
      useFactory: (locale: LocaleService) => () => locale.init(),
    },
    {
      // Purge legacy localStorage tokens, then restore the session from the
      // HttpOnly refresh cookie (BLK-30).
      //
      // BLK-36: deliberately NOT awaited. Returning this promise made the whole
      // app — login page included — wait on a `/auth/refresh-token` round-trip
      // before painting anything, and for a signed-out visitor that request is a
      // guaranteed 401. The restore still starts here, at the earliest possible
      // moment; the guards await it, which is the only place the answer is
      // actually needed. `bootstrapSession()` is single-flighted, so the guard
      // joins this attempt rather than starting a second one.
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthService],
      useFactory: (auth: AuthService) => () => {
        migrateAuthStorage();
        void auth.bootstrapSession();
        return Promise.resolve();
      },
    },
    {
      // LocaleService IS the CDK's Directionality, so every overlay the kit
      // opens — select, tooltip, sheet, dialog, datepicker — lands on the
      // correct side in Arabic without a single `[dir]` binding in a template.
      provide: Directionality,
      useExisting: LocaleService,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: { provide: TranslateLoader, useFactory: jsonLoader, deps: [HttpClient] },
      }),
      AngularFireModule.initializeApp(environment.firebase),
      AngularFireMessagingModule,
    ),
  ]
}).catch(err => console.error(err));
